/**
 * Animal reference codes for SC DPH forms.
 * Source: MavenIntegrationFormat (2025-07-10)
 * NOTE: Values (codes/descriptions) are kept exactly as provided, including duplicates/typos.
 */

export const GROUPS = {
    DOMESTIC: "DOMESTIC",
    LIVESTOCK: "LIVESTOCK",
    OTHER_DOMESTIC_LIVESTOCK: "OTHER_DOMESTIC_LIVESTOCK",
    POCKET_PET: "POCKET_PET",
    WILD_ANIMALS: "WILD_ANIMALS",
};

export const GROUP_LABELS = {
    [GROUPS.DOMESTIC]: "Domestic - Animal Rabies",
    [GROUPS.LIVESTOCK]: "Livestock - Animal Rabies",
    [GROUPS.OTHER_DOMESTIC_LIVESTOCK]: "Other Domestic Livestock - Animal Rabies",
    [GROUPS.POCKET_PET]: "Pocket Pets - Animal Rabies",
    [GROUPS.WILD_ANIMALS]: "Wild Animals - Animal Rabies",
};

/** Every ReferenceCodeDefinition from your XML (nothing skipped). */
export const ANIMAL_CODES = [
    // DOMESTIC
    { group: GROUPS.DOMESTIC, code: "CAT", description: "cat" },
    { group: GROUPS.DOMESTIC, code: "DOG", description: "dog" },
    { group: GROUPS.DOMESTIC, code: "FERRET", description: "ferret" },

    // LIVESTOCK
    { group: GROUPS.LIVESTOCK, code: "COW", description: "cow" },
    { group: GROUPS.LIVESTOCK, code: "DONKEY", description: "donkey" },
    { group: GROUPS.LIVESTOCK, code: "GOAT", description: "goat" },
    { group: GROUPS.LIVESTOCK, code: "HORSE", description: "horse" },
    { group: GROUPS.LIVESTOCK, code: "MULE", description: "mule" },
    { group: GROUPS.LIVESTOCK, code: "PIG", description: "pig" },
    { group: GROUPS.LIVESTOCK, code: "PONY", description: "pony" },
    { group: GROUPS.LIVESTOCK, code: "SHEEP", description: "sheep" },

    // OTHER_DOMESTIC_LIVESTOCK
    { group: GROUPS.OTHER_DOMESTIC_LIVESTOCK, code: "ALPACA", description: "alpaca" },
    { group: GROUPS.OTHER_DOMESTIC_LIVESTOCK, code: "ANKOLE_WATSUI", description: "ankole-watusi" },
    { group: GROUPS.OTHER_DOMESTIC_LIVESTOCK, code: "BISON", description: "bison" },
    { group: GROUPS.OTHER_DOMESTIC_LIVESTOCK, code: "BUFFALO", description: "buffalo" },
    { group: GROUPS.OTHER_DOMESTIC_LIVESTOCK, code: "CAMEL", description: "camel" },
    { group: GROUPS.OTHER_DOMESTIC_LIVESTOCK, code: "FALLOW", description: "fallow deer" },
    { group: GROUPS.OTHER_DOMESTIC_LIVESTOCK, code: "HIGHLAND", description: "highland cattle" },
    { group: GROUPS.OTHER_DOMESTIC_LIVESTOCK, code: "LLAMA", description: "llama" },
    { group: GROUPS.OTHER_DOMESTIC_LIVESTOCK, code: "PONY", description: "pony" },
    { group: GROUPS.OTHER_DOMESTIC_LIVESTOCK, code: "SHEEP", description: "sheep" },

    // POCKET_PET
    { group: GROUPS.POCKET_PET, code: "CHINCHILLA", description: "chinchilla" },
    { group: GROUPS.POCKET_PET, code: "CHINCILLA", description: "Chincilla" }, // as provided
    { group: GROUPS.POCKET_PET, code: "DOMESTIC_RABB", description: "domestic rabbits" },
    { group: GROUPS.POCKET_PET, code: "GERBIL", description: "gerbil" },
    { group: GROUPS.POCKET_PET, code: "GUINEA_PIG", description: "guinea pig" },
    { group: GROUPS.POCKET_PET, code: "HAMSTER", description: "hamster" },
    { group: GROUPS.POCKET_PET, code: "HEDGEHOG", description: "hedgehog" },
    { group: GROUPS.POCKET_PET, code: "MOUSE", description: "mouse" },
    { group: GROUPS.POCKET_PET, code: "RABBIT", description: "rabbit" },
    { group: GROUPS.POCKET_PET, code: "RAT", description: "rat" },

    // WILD_ANIMALS
    { group: GROUPS.WILD_ANIMALS, code: "ARMADILLO", description: "armadillo" },
    { group: GROUPS.WILD_ANIMALS, code: "BAT", description: "bat" },
    { group: GROUPS.WILD_ANIMALS, code: "BEAR", description: "bear" },
    { group: GROUPS.WILD_ANIMALS, code: "BEAVER", description: "beaver" },
    { group: GROUPS.WILD_ANIMALS, code: "BOBCAT", description: "bobcat" },
    { group: GROUPS.WILD_ANIMALS, code: "CHIPMUNK", description: "chipmunk" },
    { group: GROUPS.WILD_ANIMALS, code: "COYOTE", description: "coyote" },
    { group: GROUPS.WILD_ANIMALS, code: "FOX", description: "fox" },
    { group: GROUPS.WILD_ANIMALS, code: "GROUNDHOG", description: "groundhog" },
    { group: GROUPS.WILD_ANIMALS, code: "LAGOMORPHS", description: "lagomorphs (hares, rabbies, and pikas)" },
    { group: GROUPS.WILD_ANIMALS, code: "MINK", description: "mink" },
    { group: GROUPS.WILD_ANIMALS, code: "MOLE", description: "mole" },
    { group: GROUPS.WILD_ANIMALS, code: "MOUSE", description: "mouse" },
    { group: GROUPS.WILD_ANIMALS, code: "MUSKRAT", description: "muskrat" },
    { group: GROUPS.WILD_ANIMALS, code: "OPOSSUM", description: "opossum" },
    { group: GROUPS.WILD_ANIMALS, code: "RACCOON", description: "raccoon" },
    { group: GROUPS.WILD_ANIMALS, code: "RAT", description: "rat" },
    { group: GROUPS.WILD_ANIMALS, code: "RIVER_OTTER", description: "river otter" },
    { group: GROUPS.WILD_ANIMALS, code: "SKUNK", description: "skunk" },
    { group: GROUPS.WILD_ANIMALS, code: "SQUIRREL", description: "squirrel" },
    { group: GROUPS.WILD_ANIMALS, code: "WEASEL", description: "weasel" },
    { group: GROUPS.WILD_ANIMALS, code: "WHITE_TAILED", description: "white tailed deer" },
    { group: GROUPS.WILD_ANIMALS, code: "WILD_HOG", description: "wild hog" },
    { group: GROUPS.WILD_ANIMALS, code: "WOLF", description: "wolf" },
];

/** Helper label for MUI Autocomplete (`getOptionLabel`). */
export const getOptionLabel = (opt) => `${opt.description} (${opt.code})`;

/** Convenience helpers (optional). */
export const getCodesByGroup = (group) => ANIMAL_CODES.filter((o) => o.group === group);
export const findAnimalByCode = (code) =>
    ANIMAL_CODES.find((o) => o.code === code);

/** Ready-to-use flat select options (value/label) if needed elsewhere. */
export const SELECT_OPTIONS = ANIMAL_CODES.map((o) => ({
    value: `${o.group}:${o.code}`,
    label: getOptionLabel(o),
    group: GROUP_LABELS[o.group] || o.group,
}));

/* -------------------------------------------------------------------------- */
/*                          COMPREHENSIVE DOG BREEDS                           */
/* -------------------------------------------------------------------------- */
/**
 * A broad, practical list of dog breeds (AKC-recognized plus commonly seen types).
 * Use this for form dropdowns. Keep alpha order for UX.
 */
export const DOG_BREEDS = [
    "Affenpinscher",
    "Afghan Hound",
    "Airedale Terrier",
    "Akita",
    "Alaskan Malamute",
    "American Bulldog",
    "American English Coonhound",
    "American Eskimo Dog",
    "American Foxhound",
    "American Hairless Terrier",
    "American Leopard Hound",
    "American Pit Bull Terrier",
    "American Staffordshire Terrier",
    "American Water Spaniel",
    "Anatolian Shepherd Dog",
    "Appenzeller Sennenhund",
    "Australian Cattle Dog",
    "Australian Kelpie",
    "Australian Shepherd",
    "Australian Stumpy Tail Cattle Dog",
    "Australian Terrier",
    "Azawakh",
    "Barbet",
    "Basenji",
    "Basset Hound",
    "Bavarian Mountain Scent Hound",
    "Beagle",
    "Bearded Collie",
    "Beauceron",
    "Bedlington Terrier",
    "Belgian Laekenois",
    "Belgian Malinois",
    "Belgian Sheepdog",
    "Belgian Tervuren",
    "Bergamasco Sheepdog",
    "Berger Picard",
    "Bernese Mountain Dog",
    "Bichon Frise",
    "Biewer Terrier",
    "Black and Tan Coonhound",
    "Black Russian Terrier",
    "Bloodhound",
    "Blue Lacy",
    "Bluetick Coonhound",
    "Boerboel",
    "Bohemian Shepherd",
    "Border Collie",
    "Border Terrier",
    "Borzoi",
    "Boston Terrier",
    "Bouvier des Flandres",
    "Boxer",
    "Boykin Spaniel",
    "Bracco Italiano",
    "Braque du Bourbonnais",
    "Braque Francais Pyrenean",
    "Briard",
    "Brittany",
    "Broholmer",
    "Brussels Griffon",
    "Bull Terrier",
    "Bulldog",
    "Bullmastiff",
    "Cairn Terrier",
    "Canaan Dog",
    "Cane Corso",
    "Cardigan Welsh Corgi",
    "Carolina Dog",
    "Catahoula Leopard Dog",
    "Caucasian Shepherd Dog",
    "Cavalier King Charles Spaniel",
    "Central Asian Shepherd Dog",
    "Cesky Terrier",
    "Chesapeake Bay Retriever",
    "Chihuahua",
    "Chinese Crested",
    "Chinese Shar-Pei",
    "Chinook",
    "Chow Chow",
    "Cirneco dell'Etna",
    "Clumber Spaniel",
    "Cocker Spaniel",
    "Collie",
    "Coton de Tulear",
    "Curly-Coated Retriever",
    "Dachshund",
    "Dalmatian",
    "Dandie Dinmont Terrier",
    "Danish-Swedish Farmdog",
    "Doberman Pinscher",
    "Dogo Argentino",
    "Dogue de Bordeaux",
    "Drentsche Patrijshond",
    "Drever",
    "Dutch Shepherd",
    "English Cocker Spaniel",
    "English Foxhound",
    "English Setter",
    "English Springer Spaniel",
    "English Toy Spaniel",
    "Entlebucher Mountain Dog",
    "Estrela Mountain Dog",
    "Eurasier",
    "Field Spaniel",
    "Finnish Lapphund",
    "Finnish Spitz",
    "Flat-Coated Retriever",
    "French Bulldog",
    "German Longhaired Pointer",
    "German Pinscher",
    "German Shepherd Dog",
    "German Shorthaired Pointer",
    "German Wirehaired Pointer",
    "Giant Schnauzer",
    "Glen of Imaal Terrier",
    "Golden Retriever",
    "Gordon Setter",
    "Grand Basset Griffon Vend�en",
    "Great Dane",
    "Great Pyrenees",
    "Greater Swiss Mountain Dog",
    "Greyhound",
    "Hamiltonstovare",
    "Hanoverian Scenthound",
    "Harrier",
    "Havanese",
    "Hokkaido",
    "Hovawart",
    "Ibizan Hound",
    "Icelandic Sheepdog",
    "Irish Red and White Setter",
    "Irish Setter",
    "Irish Terrier",
    "Irish Water Spaniel",
    "Irish Wolfhound",
    "Italian Greyhound",
    "Jagdterrier",
    "Japanese Akitainu",
    "Japanese Chin",
    "Japanese Spitz",
    "Jindo",
    "Kai Ken",
    "Karelian Bear Dog",
    "Keeshond",
    "Kerry Blue Terrier",
    "Komondor",
    "Kromfohrlander",
    "Kuvasz",
    "Labrador Retriever",
    "Lagotto Romagnolo",
    "Lakeland Terrier",
    "Lancashire Heeler",
    "Leonberger",
    "Lhasa Apso",
    "Lowchen",
    "Maltese",
    "Manchester Terrier",
    "Maremma Sheepdog",
    "Mastiff",
    "Miniature American Shepherd",
    "Miniature Bull Terrier",
    "Miniature Pinscher",
    "Miniature Schnauzer",
    "Mountain Cur",
    "Mudi",
    "Neapolitan Mastiff",
    "Newfoundland",
    "Norfolk Terrier",
    "Norrbottenspets",
    "Norwegian Buhund",
    "Norwegian Elkhound",
    "Norwegian Lundehund",
    "Nova Scotia Duck Tolling Retriever",
    "Old English Sheepdog",
    "Otterhound",
    "Papillon",
    "Parson Russell Terrier",
    "Pekingese",
    "Pembroke Welsh Corgi",
    "Perro de Presa Canario",
    "Peruvian Inca Orchid",
    "Petit Basset Griffon Vend�en",
    "Pharaoh Hound",
    "Plott Hound",
    "Pointer",
    "Polish Lowland Sheepdog",
    "Pomeranian",
    "Poodle (Miniature)",
    "Poodle (Standard)",
    "Poodle (Toy)",
    "Porcelaine",
    "Portuguese Podengo",
    "Portuguese Podengo Pequeno",
    "Portuguese Pointer",
    "Portuguese Sheepdog",
    "Portuguese Water Dog",
    "Pudelpointer",
    "Pug",
    "Puli",
    "Pumi",
    "Pyrenean Mastiff",
    "Pyrenean Shepherd",
    "Rat Terrier",
    "Redbone Coonhound",
    "Rhodesian Ridgeback",
    "Rottweiler",
    "Russian Toy",
    "Saint Bernard",
    "Saluki",
    "Samoyed",
    "Schapendoes",
    "Schipperke",
    "Scottish Deerhound",
    "Scottish Terrier",
    "Sealyham Terrier",
    "Segugio Italiano",
    "Shetland Sheepdog",
    "Shiba Inu",
    "Shih Tzu",
    "Shikoku",
    "Siberian Husky",
    "Silky Terrier",
    "Skye Terrier",
    "Sloughi",
    "Slovakian Wirehaired Pointer",
    "Slovensky Cuvac",
    "Small Munsterlander",
    "Smooth Fox Terrier",
    "Soft Coated Wheaten Terrier",
    "Spanish Mastiff",
    "Spanish Water Dog",
    "Spinone Italiano",
    "Stabyhoun",
    "Staffordshire Bull Terrier",
    "Standard Schnauzer",
    "Sussex Spaniel",
    "Swedish Lapphund",
    "Swedish Vallhund",
    "Taiwan Dog",
    "Thai Ridgeback",
    "Tibetan Mastiff",
    "Tibetan Spaniel",
    "Tibetan Terrier",
    "Tornjak",
    "Tosa",
    "Toy Fox Terrier",
    "Transylvanian Hound",
    "Treeing Tennessee Brindle",
    "Treeing Walker Coonhound",
    "Vizsla",
    "Volpino Italiano",
    "Weimaraner",
    "Welsh Springer Spaniel",
    "Welsh Terrier",
    "West Highland White Terrier",
    "Wetterhoun",
    "Whippet",
    "Wire Fox Terrier",
    "Wirehaired Pointing Griffon",
    "Wirehaired Vizsla",
    "Working Kelpie",
    "Xoloitzcuintli",
    "Yakutian Laika",
    "Yorkshire Terrier",
];

/** Breeds by animal TYPE code (expand as you add more species-specific lists). */
export const BREEDS_BY_TYPE = {
    DOG: DOG_BREEDS,
    // CAT: [...],
    // HORSE: [...],
};

/** Helper: get breeds list for a selected TYPE code (e.g., "DOG"). */
export const getBreedsFor = (typeCode) => BREEDS_BY_TYPE[typeCode] || [];
